import { Alert, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSessionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type RouteRow = { id: string; code: string; name: string; is_active?: number | boolean; capacity?: number | null; active_riders?: number };
type RouteDetail = {
  route: { capacity?: number | null };
  stops: Array<{ id: string; code: string; name: string }>;
  active_riders?: number;
};
type Enrollment = {
  id: string;
  student_id: string;
  session_id: string;
  route_id: string;
  stop_id: string;
  effective_from: string;
  effective_to?: string | null;
  status: string;
  notes?: string | null;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
};

type FormValues = {
  student_id: string;
  session_id: string;
  route_id: string;
  stop_id: string;
  effective_from: string;
  notes?: string;
};

export default function TransportEnrollmentFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();
  const routeIdWatch = Form.useWatch('route_id', form);
  const presetStudentId = searchParams.get('student_id') || undefined;

  const existing = useQuery({
    queryKey: ['education-transport-enrollment', id],
    enabled: !isNew && Boolean(id),
    queryFn: () => api.get<Enrollment>(`/education/transport/enrollments/${id}`),
  });

  const routes = useQuery({
    queryKey: ['education-transport-routes'],
    queryFn: async () => {
      const res = await api.get<RouteRow[]>('/education/transport/routes');
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: canManage,
  });

  const routeDetail = useQuery({
    queryKey: ['education-transport-route', routeIdWatch],
    enabled: Boolean(routeIdWatch),
    queryFn: () => api.get<RouteDetail>(`/education/transport/routes/${routeIdWatch}`),
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        student_id: presetStudentId,
        effective_from: new Date().toISOString().slice(0, 10),
      });
      return;
    }
    const row = existing.data?.data;
    if (!row) return;
    form.setFieldsValue({
      student_id: row.student_id,
      session_id: row.session_id,
      route_id: row.route_id,
      stop_id: row.stop_id,
      effective_from: String(row.effective_from).slice(0, 10),
      notes: row.notes || undefined,
    });
  }, [isNew, existing.data, form, presetStudentId]);

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post('/education/transport/enrollments', values),
    onSuccess: (result) => {
      const status = (result.data as { status?: string })?.status;
      message.success(
        status === 'waitlisted' ? t('transportEnrollmentWaitlisted') : t('transportEnrollmentCreated')
      );
      queryClient.invalidateQueries({ queryKey: ['education-transport-enrollments'] });
      navigate('/education/transport/enrollments');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const update = useMutation({
    mutationFn: (values: FormValues) =>
      api.put(`/education/transport/enrollments/${id}`, {
        route_id: values.route_id,
        stop_id: values.stop_id,
        effective_from: values.effective_from,
        notes: values.notes || null,
      }),
    onSuccess: () => {
      message.success(t('transportEnrollmentUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-transport-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['education-transport-enrollment', id] });
      navigate('/education/transport/enrollments');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const activeRoutes = useMemo(
    () => (routes.data ?? []).filter((r) => !(r.is_active === 0 || r.is_active === false)),
    [routes.data]
  );
  const stopOptions = (routeDetail.data?.data?.stops ?? []).map((s) => ({
    value: s.id,
    label: `${s.code} — ${s.name}`,
  }));
  const capacity = routeDetail.data?.data?.route?.capacity;
  const riders = routeDetail.data?.data?.active_riders ?? 0;
  const enrollment = existing.data?.data;
  const ended = enrollment?.status === 'ended';

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newTransportEnrollment')} description={t('transportEnrollmentsIntro')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newTransportEnrollment') : t('editTransportEnrollment')}
      description={t('transportEnrollmentFormIntro')}
      extra={
        <Button onClick={() => navigate('/education/transport/enrollments')}>
          {t('backToTransportEnrollments')}
        </Button>
      }
    >
      <Card size="small" loading={!isNew && existing.isLoading} style={{ maxWidth: 720 }}>
        {ended ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('transportEnrollmentEndedReadonly')}
          />
        ) : null}
        {capacity != null ? (
          <Alert
            type={riders >= Number(capacity) ? 'warning' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            message={t('transportCapacityHint', { riders, capacity: Number(capacity) })}
          />
        ) : null}
        <Form
          form={form}
          layout="vertical"
          disabled={ended}
          onValuesChange={(changed) => {
            if ('route_id' in changed) form.setFieldValue('stop_id', undefined);
          }}
          onFinish={(values) => (isNew ? create.mutate(values) : update.mutate(values))}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect style={{ width: '100%' }} disabled={!isNew || Boolean(presetStudentId)} />
          </Form.Item>
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect style={{ width: '100%' }} disabled={!isNew} />
          </Form.Item>
          <Form.Item name="route_id" label={t('transportRoute')} rules={[{ required: true }]}>
            <Select
              style={{ width: '100%' }}
              loading={routes.isLoading}
              options={activeRoutes.map((r) => ({
                value: r.id,
                label: `${r.code} — ${r.name}${r.capacity != null ? ` (${r.active_riders ?? 0}/${r.capacity})` : ''}`,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="stop_id" label={t('stop')} rules={[{ required: true }]}>
            <Select
              style={{ width: '100%' }}
              options={stopOptions}
              showSearch
              optionFilterProp="label"
              disabled={!routeIdWatch}
              loading={Boolean(routeIdWatch) && routeDetail.isLoading}
            />
          </Form.Item>
          <Form.Item name="effective_from" label={t('effectiveOn')} rules={[{ required: true }]}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={create.isPending || update.isPending}
              disabled={ended}
            >
              {t('save')}
            </Button>
            <Button onClick={() => navigate('/education/transport/enrollments')}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
